import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Wallet, Plus, Trash2, Pencil, Users2, User, Loader2, ChevronLeft, ChevronRight, Plane, Settings2, Eye, EyeOff, Check, Target, Repeat, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { auth } from "../lib/firebase";
import { AppTopBar } from "../components/AppScreen";

const money = (n, ccy) => `${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${ccy}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (m, d) => {
  const [y, mo] = m.split("-").map(Number);
  const dt = new Date(Date.UTC(y, mo - 1 + d, 1));
  return dt.toISOString().slice(0, 7);
};
const monthLabel = (m) => new Date(`${m}-15`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
const inp = "w-full border border-zinc-300 px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white";
const lbl = "block text-xs font-600 text-muted-foreground mb-0.5";
const OTHER = "__other__";

const emptyForm = () => ({ date: new Date().toISOString().slice(0, 10), title: "", amount: "", currency: "USD",
  category: "", subcategory: "", payment_method: "", merchant: "", location: "", notes: "",
  new_category: "", new_subcategory: "" });

/* General Expense Manager — everyday personal & household spending, separate from Travel,
   with an option to fold travel (trip) expenses into the same plan. */
export default function ExpenseManager() {
  const [scope, setScope] = useState("me");
  const [month, setMonth] = useState(thisMonth());
  const [includeTravel, setIncludeTravel] = useState(false);
  const [data, setData] = useState(null);
  const [cats, setCats] = useState(null);
  const [modal, setModal] = useState(null); // {editId?, form}
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get("/personal-expenses/report.pdf", { params: { scope, month }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `spending-${month}-${scope}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build the PDF — please try again"); }
    setPdfBusy(false);
  };
  const [renaming, setRenaming] = useState(null); // {id, label}
  const [budget, setBudget] = useState(null);
  const [budgetEdit, setBudgetEdit] = useState(null); // {amount, currency}

  const load = useCallback(() => api.get("/personal-expenses", { params: { scope, month, include_travel: includeTravel } })
    .then((r) => { setData(r.data); setLoadError(false); })
    .catch(() => { setData({ items: [], summary: {} }); setLoadError(true); }), [scope, month, includeTravel]);
  useEffect(() => { load(); }, [load]);
  const loadCats = useCallback(() => api.get("/personal-expenses/categories").then((r) => setCats(r.data)).catch(() => setCats({ defaults: [], custom: [] })), []);
  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { api.get("/personal-expenses/budget", { params: { scope } }).then((r) => setBudget(r.data.budget)).catch(() => setBudget(null)); }, [scope]);

  const allCats = [...(cats?.defaults || []), ...(cats?.custom || []).map((c) => ({ ...c, id: c.label, subcategories: c.subcategories || [], custom: true, cid: c.id }))];
  const visibleCats = allCats.filter((c) => !c.hidden);
  const catOf = (id) => allCats.find((c) => c.id === id);

  const toggleHide = async (c) => {
    await api.post("/personal-expenses/categories/visibility", { id: c.custom ? c.cid : c.id, hidden: !c.hidden });
    loadCats();
  };
  const saveBudget = async () => {
    try {
      const r = await api.put("/personal-expenses/budget", { scope, amount: budgetEdit.amount, currency: budgetEdit.currency });
      setBudget(r.data.budget);
      setBudgetEdit(null);
      toast.success(r.data.budget ? "Budget saved" : "Budget cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save budget"); }
  };

  const save = async () => {
    const f = modal.form;
    if (!f.amount || Number(f.amount) <= 0) { toast.error("A positive amount is required"); return; }
    setBusy(true);
    try {
      let category = f.category, subcategory = f.subcategory;
      if (f.category === OTHER) {
        if (!f.new_category.trim()) { toast.error("Name your new category"); setBusy(false); return; }
        const r = await api.post("/personal-expenses/categories", { label: f.new_category, subcategory: f.new_subcategory });
        category = r.data.label;
        subcategory = f.new_subcategory || "";
        setCats((c) => ({ ...c, custom: [...(c.custom || []).filter((x) => x.id !== r.data.id), r.data] }));
      } else if (f.subcategory === OTHER) {
        subcategory = f.new_subcategory;
      }
      const body = { ...f, category: category || "other", subcategory };
      if (modal.editId) await api.put(`/personal-expenses/${modal.editId}`, body);
      else await api.post("/personal-expenses", body);
      toast.success("Expense saved");
      setModal(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/personal-expenses/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "You can only delete your own expenses"); }
  };

  const myUid = auth.currentUser?.uid;
  const travelItems = (data?.items || []).filter((e) => e.source === "travel");
  const everydayItems = (data?.items || []).filter((e) => e.source !== "travel");
  const f = modal?.form;
  const selCat = f && catOf(f.category);

  return (
    <div data-testid="expense-manager">
      <AppTopBar title="Expenses" />
      <div className="max-w-5xl mx-auto px-5 py-8">
      <h1 className="font-heading font-900 text-3xl flex items-center gap-2 text-[#b45309]"><Wallet className="w-8 h-8" /> Expense Manager</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-5">Everyday spending for you or the whole household — separate from travel, with the option to fold your trip expenses in. <Link to="/travel-hub" className="text-primary font-700 hover:underline">Back to My Travel Hub →</Link></p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="inline-flex border border-zinc-300" data-testid="em-scope">
          {[["me", "My expenses", User], ["household", "Household", Users2]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setScope(k)} data-testid={`em-scope-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-700 ${scope === k ? "bg-[#b45309] text-white" : "bg-white text-zinc-600 hover:text-zinc-900"}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
        <div className="inline-flex items-center border border-zinc-300 bg-white">
          <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="em-month-prev" className="px-2 py-2 hover:bg-zinc-50"><ChevronLeft className="w-4 h-4" /></button>
          <span className="px-2 text-sm font-700 w-36 text-center" data-testid="em-month-label">{monthLabel(month)}</span>
          <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="em-month-next" className="px-2 py-2 hover:bg-zinc-50"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <label className="inline-flex items-center gap-1.5 text-sm font-600 border border-zinc-300 bg-white px-3 py-2 cursor-pointer">
          <input type="checkbox" checked={includeTravel} onChange={(e) => setIncludeTravel(e.target.checked)} data-testid="em-include-travel" className="accent-[#b45309]" />
          <Plane className="w-4 h-4 text-sky-600" /> Include travel expenses
        </label>
        <button onClick={() => setManageOpen(true)} data-testid="em-manage-cats-btn"
          className="ml-auto inline-flex items-center gap-1.5 border border-zinc-300 bg-white px-3 py-2 text-sm font-600 hover:border-[#b45309]"><Settings2 className="w-4 h-4" /> Categories</button>
        <button onClick={downloadPdf} disabled={pdfBusy} data-testid="em-report-pdf"
          className="inline-flex items-center gap-1.5 border border-zinc-300 bg-white px-3 py-2 text-sm font-600 hover:border-[#b45309] disabled:opacity-50">
          <FileDown className="w-4 h-4" /> {pdfBusy ? "Building…" : "PDF report"}
        </button>
        <button onClick={() => setModal({ form: emptyForm() })} data-testid="em-add-btn"
          className="inline-flex items-center gap-1.5 bg-[#b45309] text-white px-4 py-2 text-sm font-700"><Plus className="w-4 h-4" /> Add expense</button>
      </div>

      {/* Travel — kept separate and prominent, never mixed into everyday spending */}
      <div className="border-2 border-sky-300 bg-sky-50/60 p-4 mb-4" data-testid="em-travel-section">
        <div className="flex flex-wrap items-center gap-2">
          <Plane className="w-5 h-5 text-sky-600 shrink-0" />
          <span className="font-heading font-900 text-sky-800">Travel Expenses</span>
          {includeTravel && Object.entries(data?.travel_summary || {}).map(([ccy, s]) => (
            <span key={ccy} className="font-mono font-900 text-lg text-sky-900" data-testid={`em-travel-total-${ccy}`}>{money(s.total, ccy)}</span>
          ))}
          <Link to="/travel-hub#hub-expenses" className="ml-auto text-xs font-700 text-sky-700 hover:underline shrink-0" data-testid="em-travel-tracker-link">
            Open Travel Expense Tracker →
          </Link>
        </div>
        {!includeTravel && (
          <p className="text-xs text-sky-800/80 mt-1">Trip spending is tracked separately in your trips. Tick "Include travel expenses" above to see this month's travel spend here — it stays in its own section, never mixed with everyday costs.</p>
        )}
        {includeTravel && travelItems.length === 0 && <p className="text-xs text-sky-800/80 mt-1" data-testid="em-travel-empty">No travel expenses for {monthLabel(month)}.</p>}
        {includeTravel && travelItems.length > 0 && (
          <div className="mt-2 divide-y divide-sky-100 bg-white border border-sky-200">
            {travelItems.map((e) => (
              <div key={`t-${e.id}`} className="p-2.5 flex flex-wrap items-center gap-2" data-testid={`em-travel-row-${e.id}`}>
                <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{e.date}</span>
                <div className="flex-1 min-w-[140px]">
                  <div className="text-sm font-700 truncate">{e.title || "Travel expense"}</div>
                  <div className="text-xs text-muted-foreground truncate">{[e.subcategory, e.payment_method, scope === "household" && e.user_id !== myUid && e.added_by_name && `by ${e.added_by_name}`].filter(Boolean).join(" · ")}</div>
                </div>
                <span className="font-mono font-700 text-sm shrink-0">{money(e.amount, e.currency)}</span>
                <Link to={`/trips/${e.trip_id}#expenses`} className="text-xs text-sky-700 font-700 hover:underline shrink-0">open trip →</Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {data && Object.entries(data.summary || {}).map(([ccy, s]) => {
        const hasBudget = budget && budget.currency === ccy && budget.amount > 0;
        const pct = hasBudget ? Math.round(s.total / budget.amount * 100) : null;
        return (
        <div key={ccy} className="border border-zinc-300 bg-white p-4 mb-3" data-testid={`em-summary-${ccy}`}>
          <div className="flex flex-wrap items-baseline gap-3 mb-2">
            <span className="font-mono font-900 text-2xl">{money(s.total, ccy)}</span>
            <span className="text-xs text-muted-foreground">everyday spending · {monthLabel(month)}{scope === "household" ? ` · household (${data.member_count} member${data.member_count === 1 ? "" : "s"})` : ""}</span>
            <button onClick={() => setBudgetEdit({ amount: budget?.amount || "", currency: budget?.currency || ccy })}
              data-testid="em-budget-btn" className="ml-auto inline-flex items-center gap-1 text-xs font-700 text-[#b45309] hover:underline">
              <Target className="w-3.5 h-3.5" /> {hasBudget ? "Edit budget" : "Set monthly budget"}
            </button>
          </div>
          {hasBudget && (
            <div className="mb-3" data-testid="em-budget-bar">
              <div className="h-2 bg-zinc-100 overflow-hidden">
                <div className={`h-full ${pct > 100 ? "bg-rose-500" : pct >= 90 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <div className="flex justify-between text-[11px] text-muted-foreground mt-0.5">
                <span>{pct}% of {money(budget.amount, ccy)} monthly budget{budget.set_by_name && scope === "household" ? ` (set by ${budget.set_by_name})` : ""}</span>
                {pct <= 100 && <span>{money(Math.max(0, budget.amount - s.total), ccy)} left</span>}
              </div>
              {pct > 100 && (
                <p className="mt-1 text-xs font-700 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1.5" data-testid="em-budget-over-alert">
                  Over budget by {money(s.total - budget.amount, ccy)} this month — time to slow down or adjust the budget.
                </p>
              )}
              {pct >= 90 && pct <= 100 && (
                <p className="mt-1 text-xs font-700 text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5" data-testid="em-budget-warn-alert">
                  Heads up — you've used {pct}% of this month's budget.
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {Object.entries(s.by_category).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([cat, amt]) => (
              <div key={cat} className="flex items-center gap-2 text-xs">
                <span className="w-32 truncate">{catOf(cat)?.label || cat.replace(/_/g, " ")}</span>
                <span className="flex-1 h-1.5 bg-zinc-100"><span className="block h-1.5 bg-[#b45309]/60" style={{ width: `${Math.round(amt / s.total * 100)}%` }} /></span>
                <span className="font-mono">{money(amt, ccy)}</span>
              </div>
            ))}
          </div>
        </div>
        );
      })}

      <div className="border border-zinc-300 bg-white divide-y divide-zinc-100" data-testid="em-list">
        {loadError && <p className="p-4 text-sm text-rose-700 bg-rose-50" data-testid="em-load-error">Couldn't load your expenses — please refresh to try again.</p>}
        {data === null && <p className="p-4 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>}
        {!loadError && data && everydayItems.length === 0 && <p className="p-4 text-sm text-muted-foreground" data-testid="em-empty">No everyday expenses for {monthLabel(month)} — add your first one.</p>}
        {everydayItems.map((e) => (
          <div key={`p-${e.id}`} className="p-3 flex flex-wrap items-center gap-2" data-testid={`em-row-${e.id}`}>
            <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{e.date}</span>
            <div className="flex-1 min-w-[160px]">
              <div className="text-sm font-700 truncate">{e.title || e.merchant || "Expense"}</div>
              <div className="text-xs text-muted-foreground truncate">
                {[catOf(e.category)?.label || (e.category || "").replace(/_/g, " "), e.subcategory, e.payment_method,
                  scope === "household" && e.user_id !== myUid && e.added_by_name && `by ${e.added_by_name}`].filter(Boolean).join(" · ")}
              </div>
            </div>
            {e.recurring === "monthly" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-800 uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-300 px-1.5 py-0.5 shrink-0" data-testid={`em-recurring-${e.id}`}
                title={e.auto_created ? "Added automatically from your monthly series" : "Repeats monthly"}>
                <Repeat className="w-3 h-3" /> {e.auto_created ? "auto · monthly" : "monthly"}
              </span>
            )}
            <span className="font-mono font-700 text-sm shrink-0" data-testid={`em-amount-${e.id}`}>{money(e.amount, e.currency)}</span>
            {e.user_id === myUid && (
              <span className="flex gap-1 shrink-0">
                <button onClick={() => setModal({ editId: e.id, form: { ...emptyForm(), ...Object.fromEntries(Object.entries(e).map(([k, v]) => [k, v == null ? "" : v])) } })}
                  data-testid={`em-edit-${e.id}`} className="p-1 text-zinc-500 hover:text-primary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(e.id)} data-testid={`em-del-${e.id}`} className="p-1 text-zinc-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
              </span>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-zinc-300 w-full max-w-lg p-5 mt-10" onClick={(e) => e.stopPropagation()} data-testid="em-modal">
            <h2 className="font-heading font-900 text-lg mb-3">{modal.editId ? "Edit expense" : "Add expense"}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Date</label><input type="date" value={f.date} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, date: e.target.value } }))} data-testid="em-f-date" className={inp} /></div>
              <div><label className={lbl}>Title</label><input value={f.title} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, title: e.target.value } }))} data-testid="em-f-title" className={inp} placeholder="Weekly groceries" /></div>
              <div><label className={lbl}>Amount</label><input type="number" step="0.01" value={f.amount} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, amount: e.target.value } }))} data-testid="em-f-amount" className={inp} /></div>
              <div><label className={lbl}>Currency</label><input maxLength={3} value={f.currency} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, currency: e.target.value.toUpperCase() } }))} data-testid="em-f-currency" className={inp} /></div>
              <div><label className={lbl}>Category</label>
                <select value={f.category} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, category: e.target.value, subcategory: "" } }))} data-testid="em-f-category" className={inp}>
                  <option value="">—</option>
                  {visibleCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  <option value={OTHER}>Other — Specify…</option>
                </select></div>
              {f.category === OTHER ? (
                <>
                  <div><label className={lbl}>New category name</label><input value={f.new_category} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, new_category: e.target.value } }))} data-testid="em-f-new-category" className={inp} placeholder="e.g. Boat Maintenance" /></div>
                  <div className="col-span-2"><label className={lbl}>First subcategory (optional)</label><input value={f.new_subcategory} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, new_subcategory: e.target.value } }))} data-testid="em-f-new-subcategory" className={inp} placeholder="e.g. Engine parts" /></div>
                </>
              ) : (
                <div><label className={lbl}>Subcategory</label>
                  <select value={f.subcategory} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, subcategory: e.target.value } }))} data-testid="em-f-subcategory" className={inp}>
                    <option value="">—</option>
                    {(selCat?.subcategories || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    <option value={OTHER}>Other — Specify…</option>
                  </select>
                  {f.subcategory === OTHER && <input value={f.new_subcategory} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, new_subcategory: e.target.value } }))} data-testid="em-f-new-sub-inline" className={`${inp} mt-1`} placeholder="Name the subcategory" />}
                </div>
              )}
              <div><label className={lbl}>Payment</label>
                <select value={f.payment_method} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, payment_method: e.target.value } }))} data-testid="em-f-payment" className={inp}>
                  <option value="">—</option>
                  {["Cash", "Credit Card", "Debit Card", "Mobile Wallet", "Bank Transfer", "Gift Card", "Coupon / Voucher", "Other"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select></div>
              <div><label className={lbl}>Merchant / store</label><input value={f.merchant} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, merchant: e.target.value } }))} data-testid="em-f-merchant" className={inp} /></div>
              <div className="col-span-2"><label className={lbl}>Notes</label><input value={f.notes} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, notes: e.target.value } }))} data-testid="em-f-notes" className={inp} /></div>
              <label className="col-span-2 flex items-start gap-2 border border-amber-300 bg-amber-50/60 p-2.5 cursor-pointer">
                <input type="checkbox" checked={f.recurring === "monthly" || f.recurring === true}
                  onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, recurring: e.target.checked ? "monthly" : "" } }))}
                  data-testid="em-f-recurring" className="accent-[#b45309] mt-0.5" />
                <span className="text-xs">
                  <span className="font-700">Repeats monthly</span> — rent, subscriptions, utilities…
                  A copy auto-appears each month on the same day. Untick (or delete the latest one) to stop.
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm border border-zinc-300">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="em-f-save" className="px-4 py-2 text-sm font-700 bg-[#b45309] text-white disabled:opacity-50">
                {busy ? "Saving…" : "Save expense"}
              </button>
            </div>
          </div>
        </div>
      )}
      {budgetEdit && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center p-4" onClick={() => setBudgetEdit(null)}>
          <div className="bg-white border border-zinc-300 w-full max-w-sm p-5 mt-24" onClick={(e) => e.stopPropagation()} data-testid="em-budget-modal">
            <h2 className="font-heading font-900 text-lg mb-1">{scope === "household" ? "Household" : "My"} monthly budget</h2>
            <p className="text-xs text-muted-foreground mb-3">Applies to everyday spending each month{scope === "household" ? " — shared with all household members" : ""}. Set 0 to remove.</p>
            <div className="flex gap-2">
              <input type="number" step="1" value={budgetEdit.amount} onChange={(e) => setBudgetEdit((b) => ({ ...b, amount: e.target.value }))}
                data-testid="em-budget-amount" className={inp} placeholder="e.g. 2000" />
              <input maxLength={3} value={budgetEdit.currency} onChange={(e) => setBudgetEdit((b) => ({ ...b, currency: e.target.value.toUpperCase() }))}
                data-testid="em-budget-currency" className={`${inp} w-20`} />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setBudgetEdit(null)} className="px-4 py-2 text-sm border border-zinc-300">Cancel</button>
              <button onClick={saveBudget} data-testid="em-budget-save" className="px-4 py-2 text-sm font-700 bg-[#b45309] text-white">Save budget</button>
            </div>
          </div>
        </div>
      )}

      {manageOpen && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={() => setManageOpen(false)}>
          <div className="bg-white border border-zinc-300 w-full max-w-lg p-5 mt-10" onClick={(e) => e.stopPropagation()} data-testid="em-manage-modal">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading font-900 text-lg">Manage categories</h2>
              <button onClick={async () => { await api.post("/personal-expenses/categories/show-all"); loadCats(); toast.success("All categories visible again"); }}
                data-testid="em-cats-show-all" className="text-xs font-700 text-[#b45309] hover:underline">Show all</button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Hide categories you never use to keep the dropdown tidy — hidden ones stay on old expenses. Custom categories can be renamed or deleted.</p>
            <div className="max-h-[55vh] overflow-y-auto divide-y divide-zinc-100 border border-zinc-200">
              {allCats.map((c) => (
                <div key={c.custom ? c.cid : c.id} className={`flex items-center gap-2 px-3 py-2 ${c.hidden ? "opacity-50" : ""}`} data-testid={`em-cat-row-${c.custom ? c.cid : c.id}`}>
                  {renaming && c.custom && renaming.id === c.cid ? (
                    <>
                      <input value={renaming.label} onChange={(e) => setRenaming((r) => ({ ...r, label: e.target.value }))}
                        data-testid="em-cat-rename-input" className={`${inp} flex-1`} autoFocus />
                      <button onClick={async () => {
                        try { await api.put(`/personal-expenses/categories/${c.cid}`, { label: renaming.label }); setRenaming(null); loadCats(); toast.success("Renamed"); }
                        catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
                      }} data-testid="em-cat-rename-save" className="p-1.5 text-emerald-600"><Check className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm font-600 truncate">{c.label}
                        {c.custom && <span className="ml-1.5 text-[10px] font-700 bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded-full">custom</span>}
                        {c.hidden && <span className="ml-1.5 text-[10px] text-muted-foreground">hidden</span>}
                      </span>
                      {c.custom && <button onClick={() => setRenaming({ id: c.cid, label: c.label })} data-testid={`em-cat-rename-${c.cid}`}
                        title="Rename" className="p-1.5 text-zinc-500 hover:text-primary"><Pencil className="w-4 h-4" /></button>}
                      <button onClick={() => toggleHide(c)} data-testid={`em-cat-hide-${c.custom ? c.cid : c.id}`}
                        title={c.hidden ? "Show in dropdown" : "Hide from dropdown"} className="p-1.5 text-zinc-500 hover:text-[#b45309]">
                        {c.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      {c.custom && <button onClick={async () => {
                        try { await api.delete(`/personal-expenses/categories/${c.cid}`); loadCats(); toast.success("Category deleted"); }
                        catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
                      }} data-testid={`em-cat-del-${c.cid}`} title="Delete custom category" className="p-1.5 text-zinc-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setManageOpen(false)} data-testid="em-manage-close" className="px-4 py-2 text-sm border border-zinc-300">Done</button>
            </div>
          </div>
        </div>
      )}

      <p className="mt-6 text-[11px] text-muted-foreground">Household view shows expenses added by every active household member; everyone edits only their own entries. Travel expenses come from your trips read-only — open the trip to change them.</p>
      </div>
    </div>
  );
}
